import React, { useCallback, useRef } from "react";
import dynamic from "next/dynamic";

import { Button, Text } from "@puzzle/ve";
import { vars } from "@puzzle/theme";
import { Paperclip } from "@puzzle/icons";

import { SidebarToggle } from "components/common/SidebarToggle";
import { uploadContent, attachmentRoot, sidebarToggle } from "./AttachmentSidebar.css";
import { attachmentSidebarText } from "./constants";
import { useAttachmentSidebar } from "./useAttachmentSidebar";
import {
  SupportedEntity,
  SupportedUploadEntity,
} from "../hooks/useAttachmentManager/useAttachmentManager";

const DynamicPDFViewer = dynamic(() =>
  import("components/common/PDFViewer").then((mod) => mod.PDFViewer)
);

const UPLOAD_ICON_SIZE = 21;

export type AttachmentSidebarProps = {
  entity?: SupportedEntity;
  uploadEntityType: SupportedUploadEntity;
  inMemoryFile?: File;
  setInMemoryFile: (file?: File) => void;
  sidebarOpen: boolean;
  toggleSidebar?: () => void;
  isEditor: boolean;
  text?: string;
};

export const AttachmentSidebar = ({
  entity,
  inMemoryFile,
  setInMemoryFile,
  isEditor,
  toggleSidebar,
  sidebarOpen,
  uploadEntityType,
  text = attachmentSidebarText.defaultUploadText,
}: AttachmentSidebarProps) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const openFilePicker = useCallback(() => hiddenFileInput.current?.click(), []);

  const { handleFiles, onDelete, dropAreaProps, over, previewFileUrl, isUploading } =
    useAttachmentSidebar({
      entity,
      uploadEntityType,
      inMemoryFile,
      setInMemoryFile,
      isEditor,
    });

  return (
    <>
      {previewFileUrl && (
        <DynamicPDFViewer
          url={previewFileUrl}
          onToggle={toggleSidebar}
          onDelete={isEditor ? onDelete : undefined}
        />
      )}
      {!previewFileUrl && (
        <div
          {...dropAreaProps}
          className={attachmentRoot[over && isEditor ? "isDropping" : "default"]}
        >
          {toggleSidebar && (
            <SidebarToggle
              open={sidebarOpen}
              onClick={() => toggleSidebar()}
              className={sidebarToggle}
            />
          )}

          <div className={uploadContent}>
            <Paperclip size={UPLOAD_ICON_SIZE} color={vars.colors["text-neutral-base"]} />
            <Text variant="headingS" weight="bold" color="neutral-moreSubdue">
              {text}
            </Text>
            <Text color="neutral-subdue" variant="bodyS">
              {attachmentSidebarText.dragAndDropText}
            </Text>
            <Button
              variant="secondary"
              size="compact"
              data-testid="browse-button"
              onClick={openFilePicker}
              disabled={!isEditor}
              loading={isUploading}
              css={{
                pointerEvents: "all",
                marginTop: `-${vars.space["1"]}`,
              }}
            >
              {attachmentSidebarText.browseButtonText}
            </Button>
          </div>
        </div>
      )}
      <input
        type="file"
        data-testid="file-input"
        ref={hiddenFileInput}
        accept=".pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          if (!e.target.files) return;
          handleFiles(e.target.files);
        }}
      />
    </>
  );
};
